import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, apiError, dateTimeOf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isParent } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, SealCheck, FileText } from "@phosphor-icons/react";

/** Printable record of one signed document: the exact version signed, answers, signer and audit details. */
export default function SignedDocument() {
  const { sigId } = useParams();
  const { user } = useAuth();
  const [sig, setSig] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/signatures/${sigId}`).then(({ data }) => setSig(data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [sigId]);

  if (error) return <div className="text-[#EF4444] font-semibold" data-testid="signed-error">{error}</div>;
  if (!sig) return <p className="text-muted-foreground" data-testid="signed-loading">Loading…</p>;
  const snap = sig.document_snapshot || {};
  const back = isParent(user) ? "/portal/forms" : `/admin/students/${sig.student_id}?tab=forms`;
  const paper = sig.kind === "paper";

  return (
    <div className="max-w-3xl" data-testid="signed-document">
      <div className="flex flex-wrap justify-between gap-3 print:hidden">
        <Link to={back} className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-[#3B82F6]" data-testid="signed-back"><ArrowLeft size={16} /> Back</Link>
        <Button variant="outline" onClick={() => window.print()} className="rounded-full font-bold" data-testid="signed-print"><Printer size={16} className="mr-2" /> Print / save PDF</Button>
      </div>

      <div className="mt-5 bg-white rounded-3xl border border-border p-8 print:border-0 print:p-0">
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Teach Play to Learn · {snap.kind}</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight" data-testid="signed-title">{sig.document_title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">Document version {sig.document_version} · for <strong>{sig.student_name}</strong></p>
        {snap.description && <p className="mt-4 text-base">{snap.description}</p>}

        <div className={`mt-6 rounded-2xl p-4 flex items-start gap-3 ${sig.status === "valid" ? (paper ? "bg-slate-100" : "bg-emerald-50") : "bg-amber-50"}`} data-testid="signed-status">
          {paper ? <FileText size={26} weight="duotone" className="text-slate-600 shrink-0" /> : <SealCheck size={26} weight="duotone" className={sig.status === "valid" ? "text-[#10B981] shrink-0" : "text-amber-600 shrink-0"} />}
          <div className="text-sm">
            <p className="font-bold">{paper ? "Staff-recorded paper signature" : sig.status === "valid" ? "Electronically signed" : "Superseded — a newer signature is required"}</p>
            {sig.status !== "valid" && <p className="text-amber-800 mt-1">{sig.superseded_reason || `Superseded ${dateTimeOf(sig.superseded_at)}`}</p>}
          </div>
        </div>

        {(snap.fields || []).filter((f) => f.type !== "signature").length > 0 && (
          <section className="mt-8" data-testid="signed-answers">
            <h2 className="font-display text-lg font-semibold">Responses</h2>
            <dl className="mt-3 divide-y divide-border">
              {(snap.fields || []).filter((f) => f.type !== "signature").map((f) => (
                <div key={f.label} className="py-2 grid sm:grid-cols-2 gap-2 text-sm"><dt className="font-bold">{f.label}</dt><dd>{String((sig.answers || {})[f.label] ?? "—") || "—"}</dd></div>
              ))}
            </dl>
          </section>
        )}

        <section className="mt-8 rounded-2xl border border-border p-5" data-testid="signed-signature">
          <h2 className="font-display text-lg font-semibold">{paper ? "Paper record" : "Signature"}</h2>
          {!paper && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{sig.statement}</p>}
          <p className="mt-4 font-display text-3xl" data-testid="signed-name">{sig.signer_name}</p>
          <dl className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[["Relationship", sig.relationship || "—"], [paper ? "Received" : "Signed", dateTimeOf(sig.signed_at)],
              [paper ? "Recorded by" : "Signed-in account", paper ? `${sig.recorded_by} (${sig.recorded_by_email})` : sig.user_email],
              ["Child", sig.student_name], ["Document version", `v${sig.document_version}`],
              [paper ? "Recorded at" : "Agreement checkbox", paper ? dateTimeOf(sig.recorded_at) : sig.agreed ? "Selected by signer" : "—"],
              ...(!paper && !isParent(user) ? [["IP address", sig.ip || "—"], ["Device", sig.user_agent || "—"]] : []),
              ["Record ID", sig.id]].map(([k, v]) => (
              <div key={k}><dt className="text-xs uppercase tracking-wider font-bold text-muted-foreground">{k}</dt><dd className="font-semibold mt-0.5 break-all">{v}</dd></div>
            ))}
          </dl>
          {paper && <p className="mt-4 text-sm text-amber-800 font-semibold">{sig.statement}{sig.note ? ` Note: ${sig.note}` : ""}</p>}
        </section>
      </div>
    </div>
  );
}
